import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { getClientPurchaseOrders } from "../../api/clientPurchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const LABEL_WIDTH = 220;
const ROW_HEIGHT = 44;
const DAY_WIDTH = 4;

/** Golden angle (deg) — sequential ids land evenly around the hue circle. */
const GOLDEN_ANGLE = 137.508;

/** Deterministic hex color from client id (stable across sessions). */
const colorFromClientId = (clientId) => {
  const id = Number(clientId) || 0;
  // Golden-angle stepping avoids clustering at 0° / 120° / 240° for sequential ids
  const hue = ((id * GOLDEN_ANGLE) % 360 + 360) % 360;
  const saturation = 55 + (id % 21); // 55–75%
  const lightness = 40 + (id % 16); // 40–55%

  const s = saturation / 100;
  const l = lightness / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (hue < 60) [r, g, b] = [c, x, 0];
  else if (hue < 120) [r, g, b] = [x, c, 0];
  else if (hue < 180) [r, g, b] = [0, c, x];
  else if (hue < 240) [r, g, b] = [0, x, c];
  else if (hue < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (n) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getWindowBounds = () => {
  const year = dayjs().year();
  return {
    rangeStart: dayjs(`${year - 1}-01-01`).startOf("day"),
    rangeEnd: dayjs(`${year + 1}-12-31`).startOf("day"),
  };
};

const CPOGanttChart = ({ companyId }) => {
  const { openSnack } = useSnack();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getClientPurchaseOrders({
        companyId,
        showCompleted,
        pageSize: 1000,
      });
      setRowData(response.data || []);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId, showCompleted]);

  const { rangeStart, rangeEnd } = useMemo(() => getWindowBounds(), []);

  const chartRows = useMemo(() => {
    return rowData
      .filter(
        (row) =>
          row.receptionDate &&
          row.requestedDeliveryDate &&
          dayjs(row.receptionDate).isValid() &&
          dayjs(row.requestedDeliveryDate).isValid()
      )
      .map((row) => {
        const start = dayjs(row.receptionDate).startOf("day");
        const end = dayjs(row.requestedDeliveryDate).startOf("day");
        const safeEnd = end.isBefore(start) ? start : end;
        return {
          ...row,
          start,
          end: safeEnd,
        };
      })
      // Keep bars that overlap the 3-year window
      .filter(
        (row) =>
          !row.end.isBefore(rangeStart, "day") &&
          !row.start.isAfter(rangeEnd, "day")
      )
      .sort((a, b) => a.end.valueOf() - b.end.valueOf());
  }, [rowData, rangeStart, rangeEnd]);

  const clientLegend = useMemo(() => {
    const byId = new Map();
    chartRows.forEach((row) => {
      const id = row.clientId ?? row.client?.id;
      if (id == null || byId.has(id)) return;
      byId.set(id, {
        id,
        name: row.client?.name || `Cliente ${id}`,
        color: colorFromClientId(id),
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es")
    );
  }, [chartRows]);

  const { totalDays, monthMarkers, todayOffset } = useMemo(() => {
    const days = Math.max(rangeEnd.diff(rangeStart, "day"), 1);

    const markers = [];
    let cursor = rangeStart.startOf("month");
    while (cursor.isBefore(rangeEnd) || cursor.isSame(rangeEnd, "month")) {
      const offsetDays = cursor.diff(rangeStart, "day");
      if (offsetDays >= 0 && offsetDays <= days) {
        markers.push({
          key: cursor.format("YYYY-MM"),
          label: cursor.format("MMM YYYY"),
          left: offsetDays * DAY_WIDTH,
        });
      }
      cursor = cursor.add(1, "month");
    }

    const today = dayjs().startOf("day");
    const todayDays = today.diff(rangeStart, "day");
    const todayPos =
      todayDays >= 0 && todayDays <= days ? todayDays * DAY_WIDTH : null;

    return {
      totalDays: days,
      monthMarkers: markers,
      todayOffset: todayPos,
    };
  }, [rangeStart, rangeEnd]);

  const timelineWidth = totalDays * DAY_WIDTH;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        useFlexGap
      >
        <Button onClick={() => setShowCompleted((prev) => !prev)}>
          {showCompleted ? "Ocultar completados" : "Mostrar completados"}
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {clientLegend.map((client) => (
            <Chip
              key={client.id}
              label={client.name}
              size="small"
              sx={{
                bgcolor: client.color,
                color: "#fff",
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      </Stack>

      {chartRows.length === 0 ? (
        <Typography color="text.secondary">
          No hay órdenes con fechas de recepción y entrega solicitada en la
          ventana {rangeStart.year()}–{rangeEnd.year()}.
        </Typography>
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "auto",
            maxHeight: 640,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ minWidth: LABEL_WIDTH + timelineWidth }}>
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                top: 0,
                zIndex: 2,
                bgcolor: "grey.100",
                borderBottom: 1,
                borderColor: "divider",
                height: 40,
              }}
            >
              <Box
                sx={{
                  width: LABEL_WIDTH,
                  minWidth: LABEL_WIDTH,
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "grey.100",
                  borderRight: 1,
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  Orden / Cliente
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: timelineWidth,
                  minWidth: timelineWidth,
                }}
              >
                {monthMarkers.map((marker) => (
                  <Typography
                    key={marker.key}
                    variant="caption"
                    sx={{
                      position: "absolute",
                      left: marker.left,
                      top: 10,
                      pl: 0.5,
                      borderLeft: 1,
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    {marker.label}
                  </Typography>
                ))}
              </Box>
            </Box>

            {chartRows.map((row) => {
              const clippedStart = row.start.isBefore(rangeStart)
                ? rangeStart
                : row.start;
              const clippedEnd = row.end.isAfter(rangeEnd)
                ? rangeEnd
                : row.end;
              const startOffset =
                clippedStart.diff(rangeStart, "day") * DAY_WIDTH;
              const durationDays = Math.max(
                clippedEnd.diff(clippedStart, "day"),
                1
              );
              const fullDurationDays = Math.max(
                row.end.diff(row.start, "day"),
                1
              );
              const barWidth = durationDays * DAY_WIDTH;
              const clientId = row.clientId ?? row.client?.id;
              const barColor = colorFromClientId(clientId);
              const label = `${row.code || "Sin PO"} · ${
                row.client?.name || "Sin cliente"
              }`;

              return (
                <Box
                  key={row.id}
                  sx={{
                    display: "flex",
                    height: ROW_HEIGHT,
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: LABEL_WIDTH,
                      minWidth: LABEL_WIDTH,
                      px: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderRight: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      title={label}
                      fontWeight={500}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      title={(row.details || [])
                        .map((d) => d.finishedGood?.code)
                        .filter(Boolean)
                        .join(", ")}
                    >
                      {(row.details || [])
                        .map((d) => d.finishedGood?.code)
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      width: timelineWidth,
                      minWidth: timelineWidth,
                      bgcolor: "grey.50",
                    }}
                  >
                    {monthMarkers.map((marker) => (
                      <Box
                        key={`${row.id}-${marker.key}`}
                        sx={{
                          position: "absolute",
                          left: marker.left,
                          top: 0,
                          bottom: 0,
                          borderLeft: "1px dashed",
                          borderColor: "divider",
                          pointerEvents: "none",
                        }}
                      />
                    ))}

                    {todayOffset != null && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: todayOffset,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          bgcolor: "error.light",
                          opacity: 0.7,
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      />
                    )}

                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2">{label}</Typography>
                          <Typography variant="caption" display="block">
                            Recepción: {row.start.format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Entrega solicitada: {row.end.format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Estado: {row.status?.name || "-"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Duración: {fullDurationDays} día
                            {fullDurationDays === 1 ? "" : "s"}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: startOffset,
                          top: 10,
                          width: Math.max(barWidth, 6),
                          height: 24,
                          borderRadius: 1,
                          bgcolor: barColor,
                          boxShadow: 1,
                          cursor: "default",
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          overflow: "hidden",
                        }}
                      >
                        {barWidth > 80 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#fff",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              textShadow: "0 0 2px rgba(0,0,0,0.4)",
                            }}
                          >
                            {row.client?.name || ""}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        Color por cliente. Ventana: {rangeStart.format("DD/MM/YYYY")} –{" "}
        {rangeEnd.format("DD/MM/YYYY")} (año actual ± 1). Línea roja: hoy (
        {dayjs().format("DD/MM/YYYY")}).
      </Typography>
    </Box>
  );
};

export default CPOGanttChart;
